import React from "react";
import { IconButton, Slider, Paper, Typography } from "@material-ui/core";
import { PlayArrow, Pause, SkipNext, SkipPrevious, Close } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useAudioPlayer } from "../../context/AudioPlayerContext";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'fixed',
    left: 16,
    right: 'auto',
    bottom: 16,
    zIndex: 1400,
    borderRadius: 12,
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    boxShadow: theme.shadows[6],
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    maxWidth: 400,
    width: 'calc(100vw - 32px)'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  contactName: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    cursor: 'pointer',
    '&:hover': { textDecoration: 'underline' },
  },
  middleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  progress: {
    flex: 1,
    margin: '0 4px',
  },
  time: {
    minWidth: 38,
    textAlign: 'center',
  },
  controls: {
    display: 'flex', alignItems: 'center', gap: 4
  }
}));

const formatTime = (time) => {
  if (!time || isNaN(time)) return '0:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const GlobalAudioPlayer = () => {
  const classes = useStyles();
  const player = useAudioPlayer();
  const history = useHistory();

  if (!player || !player.currentTrack) return null;

  const { currentTrack, isPlaying, play, pause, next, prev, currentTime, duration, seek, setVol, volume } = player;
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const isUuid = (v) => typeof v === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v);
  const goToConversation = () => {
    const maybeUuid = currentTrack.ticketUuid || currentTrack.ticketRouteId;
    if (isUuid(maybeUuid)) {
      history.push(`/tickets/${maybeUuid}`);
      return;
    }
    // Sem UUID confiável, evita quebrar backend e volta para lista
    history.push('/tickets');
  };

  return (
    <Paper className={classes.container} elevation={6}>
      <div className={classes.header}>
        <Typography className={classes.contactName} onClick={goToConversation} title={currentTrack?.contactName}>
          {currentTrack?.contactName || currentTrack?.ticketTitle || 'Conversar'}
        </Typography>
        <IconButton onClick={player.stop} size="small" aria-label="Fechar"><Close /></IconButton>
      </div>

      <div className={classes.middleRow}>
        <div className={classes.controls}>
          <IconButton onClick={prev} size="small" aria-label="Anterior"><SkipPrevious /></IconButton>
          <IconButton onClick={isPlaying ? pause : play} color="primary" aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}>
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          <IconButton onClick={next} size="small" aria-label="Próximo"><SkipNext /></IconButton>
        </div>
        <Typography variant="caption" className={classes.time}>{formatTime(currentTime)}</Typography>
        <Slider
          className={classes.progress}
          value={progress}
          onChange={(_, v) => {
            const pct = Array.isArray(v) ? v[0] : v;
            const t = (pct / 100) * (duration || 0);
            seek(t);
          }}
        />
        <Typography variant="caption" className={classes.time}>{formatTime(duration)}</Typography>
      </div>
    </Paper>
  );
};

export default GlobalAudioPlayer;
